import {format, formatDay} from 'src/lib/dates/format';
import {ScheduledStop} from 'src/services/api/types/types';
import {useTranslation} from 'react-i18next';
import {useOptionalLocationDetailsContext} from 'src/context/LocationDetailsContext';
import {getLocaleWithRegionCode} from 'src/services/i18n/locale';
import {ReactComponent as IconInfo} from 'src/components/icons/icon_info.svg';
import './ScheduledStops.scss';
import {Fragment, useState} from 'react';

interface ScheduledStopsProps {
    scheduledStops: Array<ScheduledStop>;
    includeInfo: boolean;
}

export function ScheduledStops({scheduledStops, includeInfo}: ScheduledStopsProps) {
    const {t} = useTranslation();
    const activeLocale = getLocaleWithRegionCode();
    const currentTime = Date.now();
    const sortedScheduledStops = scheduledStops
        .filter((stop) => new Date(stop.to).getTime() > currentTime)
        .sort((a, b) => new Date(a.from).getTime() - new Date(b.from).getTime());

    const getVisitReasonsForLocale = (stop: ScheduledStop) => {
        return (stop.visitReasons?.[activeLocale] ?? []).map((visitReason) => visitReason.name).join(', ');
    };

    const locationDetails = useOptionalLocationDetailsContext();
    const _hasTimebook = includeInfo && !!locationDetails?.locationDataState.hasBooking;
    const _hasDropin = includeInfo && !!locationDetails?.locationDataState.hasDropin;

    const getPossibleBookingOptions = () => {
        if (_hasDropin && _hasTimebook) {
            return '';
        }
        if (_hasDropin) {
            return t('clinic_popup.booking_options.only_dropin');
        }
        if (_hasTimebook) {
            return t('clinic_popup.booking_options.only_timebook');
        }
    };

    const heading = includeInfo
        ? t('location_popup.upcoming_stops_heading_other')
        : t('location_popup.next_stops_heading_other');

    // Note: Only one information message per interval will be shown due to the filtering of unique location stops
    // (e.g. if bus A and B have external info, only the message from bus A will show)
    const [isOpen, setIsOpen] = useState<Array<number>>([]);
    const toggleInfoBox = (locationStopIndex: number) => {
        setIsOpen((prev) =>
            prev.includes(locationStopIndex)
                ? prev.filter((index) => index !== locationStopIndex)
                : [...prev, locationStopIndex]
        );
    };

    const [showAllStops, setShowAllStops] = useState(false);

    return (
        <>
            <p className='scheduled-stops-info'>
                <b>{heading}</b>
            </p>
            <div className='scheduled-stops'>
                {sortedScheduledStops.slice(0, showAllStops ? undefined : 5).map((stop, index) => {
                    const fromDate = new Date(stop.from);
                    const toDate = new Date(stop.to);
                    return (
                        <Fragment key={`${stop.from}-${index}`}>
                            <div className='scheduled-stop-day'>{formatDay(fromDate, t)}</div>
                            <div className='scheduled-stop-time'>
                                <span>
                                    {format(fromDate, 'HH:mm')} - {format(toDate, 'HH:mm')}
                                </span>
                                {includeInfo && (
                                    <IconInfo
                                        className='scheduled-stop-info-icon'
                                        aria-hidden
                                        style={{visibility: stop.information !== '' ? 'visible' : 'hidden'}}
                                        onClick={() => toggleInfoBox(index)}
                                    />
                                )}
                            </div>
                            <div className='scheduled-stop-reason'>{getVisitReasonsForLocale(stop)}</div>
                            <div className='scheduled-stop-offer'>{getPossibleBookingOptions()}</div>

                            {isOpen.includes(index) && stop.information !== '' && (
                                <div className='toggle-stop-info'>
                                    <div className='scheduled-stop-info-text'>
                                        {t('general.information')}: {stop.information}
                                    </div>
                                    <div className='scheduled-stop-info-close'>
                                        <button onClick={() => toggleInfoBox(index)}>{t('button_close.text')}</button>
                                    </div>
                                </div>
                            )}
                        </Fragment>
                    );
                })}
            </div>
            {sortedScheduledStops.length > 5 && (
                <button
                    type='button'
                    className='scheduled-stops-toggle'
                    onClick={() => setShowAllStops((prev) => !prev)}
                >
                    {showAllStops ? t('vaccinations_show_fewer.text') : t('vaccinations_show_more.text')}
                </button>
            )}
        </>
    );
}
